import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import ScaleKeyboard from "../components/ScaleKeyboard";
import SongStatusToggle, { STATUSES } from "../components/SongStatusToggle";
import { useMastery } from "../context/MasteryContext";
import { useInstrument } from "../context/InstrumentContext";
import { useT } from "../context/LanguageContext";
import InstrumentTabs from "../components/InstrumentTabs";
import { Music2 } from "lucide-react";

const KEYS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const STATUS_MAP = STATUSES.reduce((a, s) => ({ ...a, [s.id]: s }), {});

export default function ScalesPage() {
  const [scales, setScales] = useState([]);
  const [active, setActive] = useState("C");
  const t = useT();
  const { celebrate } = useMastery() || {};
  const { current: currentInstrument, label: instLabel, emoji: instEmoji } = useInstrument();

  useEffect(() => {
    apiClient.get(`/scales?instrument=${encodeURIComponent(currentInstrument || "piano")}`).then((r) => setScales(r.data.scales));
  }, [currentInstrument]);

  const updateStatus = async (key, status) => {
    const prevStatus = scales.find((s) => s.key === key)?.status || "not_started";
    await apiClient.put("/scales", { key, status, instrument: currentInstrument || "piano" });
    setScales((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (idx < 0) return [...prev, { key, status }];
      const copy = [...prev]; copy[idx] = { ...copy[idx], status }; return copy;
    });
    if (status === "perfection" && prevStatus !== "perfection") {
      celebrate?.({ kind: "scale", itemName: key });
    }
  };

  const statusOf = (key) => scales.find((s) => s.key === key)?.status || "not_started";

  return (
    <div className="space-y-6" data-testid="scales-page">
      <InstrumentTabs />
      {!currentInstrument ? (
        <div className="bento-card p-10 text-center" data-testid="scales-no-instrument">
          <div className="text-5xl mb-3">🎼</div>
          <p className="font-display text-xl mb-2">{t("Add an instrument to start tracking scales.")}</p>
          <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
            {t("Head to")} <b>{t("My Instruments")}</b> {t("and tap \"Add instrument\" to get started")}.
          </p>
        </div>
      ) : (<>
      <div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Music2 className="w-8 h-8" /> {t("My Scales")}</h1>
        <p style={{ color: "var(--app-text-muted)" }}>{instEmoji(currentInstrument)} {instLabel(currentInstrument)} — {t("pick a key, tap a status to log your progress, the keyboard below lights up the notes.")}</p>
      </div>

      {/* 1. 12 key chips */}
      <div className="bento-card p-5">
        <div className="flex flex-wrap gap-2" data-testid="scale-chips">
          {KEYS.map((k) => {
            const status = statusOf(k);
            const meta = STATUS_MAP[status];          // undefined for 'not_started'
            const isActive = k === active;
            const bg = meta ? meta.color : "var(--app-surface-2)";
            const border = isActive ? "var(--app-primary)" : (meta ? meta.color : "var(--app-border)");
            return (
              <button
                key={k}
                onClick={() => setActive(k)}
                data-testid={`scale-chip-${k}`}
                title={meta ? meta.label : t("Not started")}
                className="rounded-2xl px-3 py-2 border-2 font-display font-bold transition-all flex items-center gap-2 hover:-translate-y-0.5"
                style={{
                  background: bg,
                  color: "var(--app-text)",
                  borderColor: border,
                  boxShadow: isActive ? "0 0 0 3px rgba(0,119,182,0.25)" : "none",
                  minWidth: "3.5rem",
                }}
              >
                <span className="text-lg" aria-hidden>{meta ? meta.icon : "🎵"}</span>
                <span className="text-base">{k}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Status buttons for the active scale (also acts as the legend) */}
      <div className="bento-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="font-display font-bold text-xl">{active} {t("major — your progress")}</h2>
          <span className="text-xs" style={{ color: "var(--app-text-muted)" }}>{t("Tap to update")}</span>
        </div>
        <SongStatusToggle value={statusOf(active)} onChange={(v) => updateStatus(active, v)} testIdPrefix={`scale-${active}`} />
      </div>

      {/* 3. Large keyboard display */}
      <div className="bento-card p-5 overflow-x-auto flex justify-center" style={{ background: "var(--app-surface-2)" }}>
        <ScaleKeyboard rootKey={active} />
      </div>
      </>)}
    </div>
  );
}
